import React from 'react';
import {
  Box,
  Flex,
  HStack,
  Link,
  Button,
  useDisclosure,
  IconButton,
  Container,
  Stack,
  Image,
  Text,
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import { NavItem } from '../types';
import NavLink from './NavLink';

/**
 * Props for the NavbarPresentation component
 */
export interface NavbarPresentationProps {
  /** Whether the navbar is in scrolled state */
  isScrolled: boolean;
  
  /** Whether the logo image loaded successfully */
  logoLoaded: boolean;
  
  /** Function to set the logo loaded state */
  setLogoLoaded: (loaded: boolean) => void;
  
  /** Logo source URL */
  logoSrc: string;
  
  /** Navigation items to display */
  navItems: NavItem[];
}

/**
 * Presentation component for the Navbar, responsible only for rendering
 */
const NavbarPresentation: React.FC<NavbarPresentationProps> = ({
  isScrolled,
  logoLoaded,
  setLogoLoaded,
  logoSrc,
  navItems
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  return (
    <Box 
      as="header" 
      pos="sticky" 
      top={0} 
      zIndex={1000} 
      bg={isScrolled ? 'white' : 'whiteAlpha.900'} 
      boxShadow={isScrolled ? 'sm' : 'none'}
      transition="all 0.3s ease"
    >
      <Container maxW="container.xl" py={{ base: 2, md: 3 }}>
        <Flex h={{ base: 14, md: 16 }} alignItems="center" justifyContent="space-between">
          <IconButton
            size="sm"
            icon={isOpen ? <CloseIcon boxSize="16px" /> : <HamburgerIcon boxSize="16px" />}
            aria-label="Open Menu"
            display={{ md: 'none' }}
            onClick={isOpen ? onClose : onOpen}
          />
          
          <HStack spacing={{ base: 4, md: 6 }} alignItems="center">
            {/* Logo with text side by side */}
            <Box>
              <Link href="/" _hover={{ textDecoration: 'none' }}>
                <HStack spacing={2} alignItems="center">
                  {/* Logo image */}
                  <Box 
                    w={{ base: "34px", md: "36px" }} 
                    h={{ base: "34px", md: "36px" }} 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Image 
                      src={logoSrc}
                      alt="AZ1.AI Logo" 
                      maxH={{ base: "34px", md: "36px" }} 
                      maxW={{ base: "34px", md: "36px" }}
                      objectFit="contain"
                    />
                  </Box>
                  
                  {/* Company name always displayed */}
                  <Text 
                    as="span" 
                    bgGradient="linear(to-r, brand.primary.dark, brand.primary.base)"
                    bgClip="text"
                    fontSize={{ base: "xl", md: "2xl" }}
                    fontWeight="extrabold"
                    ml={0}
                    display="block"
                  >
                    AZ1.AI
                  </Text>
                </HStack>
              </Link>
            </Box>
            
            {/* Desktop navigation */}
            <HStack as="nav" spacing={4} display={{ base: 'none', md: 'flex' }}>
              {navItems.map((item) => (
                <NavLink 
                  key={item.name} 
                  path={item.path}
                  isExternal={item.external}
                >
                  {item.name}
                </NavLink>
              ))}
            </HStack>
          </HStack>
          
          {/* Contact button */}
          <Flex alignItems="center">
            <Button
              size="sm"
              py={{ base: 1, md: 1.5 }}
              px={{ base: 3, md: 4 }}
              mr={3}
              display={{ base: 'none', md: 'inline-flex' }}
              bg="brand.primary.base"
              color="white"
              fontSize="sm"
              fontWeight="medium"
              _hover={{
                bg: "brand.primary.light",
              }}
              as="a"
              href="#contact"
            >
              Contact Us
            </Button>
          </Flex>
        </Flex>

        {/* Mobile menu */}
        {isOpen ? (
          <Box pb={1} pt={0} display={{ md: 'none' }}>
            <Stack as="nav" spacing={0.5}>
              {navItems.map((item) => (
                <NavLink 
                  key={item.name} 
                  path={item.path}
                  isExternal={item.external}
                  isMobile={true}
                >
                  {item.name}
                </NavLink>
              ))}
            </Stack>
          </Box>
        ) : null}
      </Container>
    </Box>
  );
};

export default NavbarPresentation;